import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { createEditor } from 'slate';
import { withHistory } from 'slate-history';
import { Slate, withReact } from 'slate-react';

import { useDecoratePositionedMarks } from 'src/components/RichSlate';
import { TranslatedTooltipWrapper } from 'src/components/TranslatedTooltipWrapper';
import { createEditorInstanceImperativeHandle } from 'src/lib/slate';

import MarkedEditable from './components/MarkedEditable';
import { SlateErrorBoundary } from './components/SlateErrorBoundary';
import { withDisabledNewlines, withMarkUserChanges } from './utils';

const propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape(),
  value: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onFocusChange: PropTypes.func,
  markUserInput: PropTypes.bool,
  disableNewlines: PropTypes.bool,
  marks: PropTypes.arrayOf(PropTypes.shape()),
  hideMarks: PropTypes.bool,
  setEditor: PropTypes.func,
  disableChildrenRewrite: PropTypes.bool,
  onCopy: PropTypes.func,
  onCut: PropTypes.func,
};

const defaultProps = {
  className: null,
  classes: null,
  readOnly: false,
  onFocusChange: null,
  markUserInput: true,
  disableNewlines: false,
  marks: [],
  hideMarks: false,
  setEditor: null,
  disableChildrenRewrite: false,
  onCopy: null,
  onCut: null,
};

const MarkedEditor = forwardRef(
  (
    {
      className,
      classes,
      value,
      readOnly,
      onChange,
      onFocusChange,
      markUserInput,
      disableNewlines,
      marks,
      hideMarks,
      setEditor,
      disableChildrenRewrite,
      onCopy,
      onCut,
      ...restProps
    },
    ref
  ) => {
    const editor = useMemo(() => {
      // Plain editor + react wrapper
      let result = withReact(createEditor());

      // If we want to mark all user edits - wrap in custom plugin
      if (markUserInput) {
        result = withMarkUserChanges(result);
      }

      if (disableNewlines) {
        result = withDisabledNewlines(result);
      }

      // Support undo-redo
      result = withHistory(result);

      return result;
    }, [markUserInput, disableNewlines]);

    const decorate = useDecoratePositionedMarks({ editor, markup: marks });

    useImperativeHandle(ref, () => createEditorInstanceImperativeHandle(editor), [editor]);

    // "The Slate Provider's "value" prop is now only used as initial state for editor.children as was intended before.
    //  If your code relies on replacing editor.children you should do so by replacing it directly instead of relying on the "value" prop to do this for you."
    // https://github.com/ianstormtaylor/slate/releases/tag/slate-react%400.67.0
    // https://github.com/ianstormtaylor/slate/pull/4540
    // https://github.com/ianstormtaylor/slate/issues/4710
    if (!disableChildrenRewrite) {
      editor.children = value;
    }

    useEffect(() => {
      if (setEditor) {
        setEditor(editor);
      }
    }, [editor, setEditor]);

    return (
      <TranslatedTooltipWrapper>
        <SlateErrorBoundary>
          <Slate
            editor={editor}
            initialValue={value}
            onValueChange={onChange}
            data-testid="MarkedEditor__Slate"
          >
            <MarkedEditable
              className={cx(className)}
              classes={classes}
              readOnly={readOnly}
              decorate={decorate}
              hideMarks={hideMarks}
              onFocusChange={onFocusChange}
              {...restProps}
              onCopy={(e) => {
                if (onCopy) {
                  onCopy(editor);
                  e.preventDefault();
                }
              }}
              onCut={() => {
                // oncut that is passed here is actually a copy handler so we should not prevent default
                if (onCut) {
                  onCut(editor);
                }
              }}
            />
          </Slate>
        </SlateErrorBoundary>
      </TranslatedTooltipWrapper>
    );
  }
);

MarkedEditor.propTypes = propTypes;
MarkedEditor.defaultProps = defaultProps;

export default MarkedEditor;
